import { createContext, useContext, type ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import type { Language } from "./translations";

type LanguageContextType = {
  lang: Language;
  dir: "rtl" | "ltr";
  switchLanguage: () => void;
  t: (section: { ar: any; en: any }) => any;
  localePath: (path: string) => string;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { lang: paramLang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const lang: Language = paramLang === "en" ? "en" : "ar";
  const dir = lang === "ar" ? "rtl" : "ltr";

  const switchLanguage = () => {
    const newLang = lang === "ar" ? "en" : "ar";
    const pathWithoutLang = location.pathname.replace(/^\/(ar|en)/, "");
    navigate(`/${newLang}${pathWithoutLang || ""}`);
  };

  const t = (section: { ar: any; en: any }) => section[lang];

  const localePath = (path: string) => `/${lang}${path}`;

  return (
    <LanguageContext.Provider value={{ lang, dir, switchLanguage, t, localePath }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
